import React, { useContext } from 'react';
import { useDeps } from 'src/hooks/useDeps';
import { BaseProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { ConfigContext } from 'src/components/AppShell/ConfigProvider';

import { Schema } from '../Schema/Schema';
import { MediaEntry, ProductPrice } from '../../types/global-types';

type Props = {
    product: BaseProduct;
    productPrice?: ProductPrice;
    material?: string;
    fitStyle?: string;
    brandName?: string;
    logoUrl?: string;
};

export const ProductSchema: React.FC<Props> = React.memo(
    ({ product, productPrice, material, fitStyle, brandName, logoUrl }) => {
        const { DOMAIN } = useDeps().env;
        const { currentCurrency } = useContext(ConfigContext);

        return (
            <Schema
                input={{
                    '@context': 'http://schema.org',
                    '@type': 'Product',
                    ...getImages(product.media_entries),
                    name: product.name,
                    sku: product.sku,
                    url: `https://${DOMAIN}/${product.url_key}`,
                    description: product.description,
                    offers: {
                        '@type': 'Offer',
                        url: `https://${DOMAIN}/${product.url_key}`,
                        priceCurrency: currentCurrency.code,
                        price: productPrice?.price_incl,
                        availability: 'http://schema.org/InStock',
                        seller: {
                            '@type': 'Organization',
                            name: 'Workwear Express',
                        },
                    },
                    brand: {
                        '@type': 'Brand',
                        name: brandName,
                    },
                    material: material,
                    additionalProperty: fitStyle && {
                        '@type': 'PropertyValue',
                        name: 'Fit',
                        value: fitStyle,
                    },
                    category: product.categories?.[0]?.name || 'no category',
                    logo: logoUrl,
                    itemCondition: 'https://schema.org/NewCondition',
                    manufacturer: brandName
                        ? {
                              '@type': 'Organization',
                              name: brandName,
                          }
                        : undefined,
                }}
            />
        );
    },
);

function getImages(mediaEntries: MediaEntry[]): { image: string[] } | undefined {
    if (!mediaEntries.length) return undefined;
    return {
        image: mediaEntries.map((image) => `${image.medium_url}`),
    };
}
